import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  Code2,
  Database,
  Globe2,
  Layers3,
  LayoutDashboard,
  MonitorSmartphone,
  Rocket,
  ShoppingCart,
  Smartphone,
  Zap,
} from "lucide-react";

import FAQSection from "./FAQSection";


const projects = [
  {
    icon: LayoutDashboard,
    title: "Cloud HRMS Platform",
    category: "SaaS Human Resource Management",
    description:
      "A cloud-based HRMS platform designed to centralize employee management, attendance, leave, payroll workflows and workforce reporting.",
    challenge:
      "Managing employee records, attendance, leave and payroll across disconnected tools creates unnecessary manual work and reduces operational visibility.",
    solution:
      "A centralized SaaS HR platform brings employee information and essential HR workflows into one connected system accessible through a modern web interface.",
    features: [
      "Employee Management",
      "Attendance & Leave",
      "Payroll Workflow",
      "HR Reports & Analytics",
    ],
    technologies: ["React", "Node.js", "Cloud", "REST API"],
  },

  {
    icon: BarChart3,
    title: "GST Billing & Invoicing Platform",
    category: "SaaS Billing Software",
    description:
      "Cloud-based billing and invoicing software for managing GST invoices, customers, products, payments and business transactions from one platform.",
    challenge:
      "Manual invoicing and fragmented transaction records can slow daily operations and make payment tracking and business reporting difficult.",
    solution:
      "A centralized billing platform organizes invoicing, customer records, products, payments and reporting into a streamlined digital workflow.",
    features: [
      "GST Invoice Generation",
      "Customer & Product Management",
      "Payment Tracking",
      "Sales & Business Reports",
    ],
    technologies: ["React", "Node.js", "Database", "Cloud"],
  },

  {
    icon: Database,
    title: "Business ERP Platform",
    category: "Enterprise SaaS & ERP",
    description:
      "A modular business management platform designed to connect operational workflows, inventory, sales, purchasing and business reporting.",
    challenge:
      "As organizations grow, business information often becomes distributed across departments, spreadsheets and disconnected applications.",
    solution:
      "A centralized ERP architecture connects important business functions and provides teams with better operational visibility through shared workflows and data.",
    features: [
      "Business Operations",
      "Inventory Management",
      "Sales & Purchase",
      "Reports & Analytics",
    ],
    technologies: ["React", "Node.js", "SQL", "REST API"],
  },

  {
    icon: ShoppingCart,
    title: "Cloud E-Commerce Platform",
    category: "Digital Commerce Software",
    description:
      "A scalable e-commerce platform designed for product discovery, online transactions, customer experiences and digital order management.",
    challenge:
      "Businesses need fast and mobile-friendly commerce experiences while maintaining efficient product, payment and order operations.",
    solution:
      "A connected commerce platform combines product management, shopping, checkout, payments and order workflows within a scalable digital environment.",
    features: [
      "Product Catalog",
      "Shopping Cart",
      "Payment Integration",
      "Order Management",
    ],
    technologies: ["React", "Node.js", "Payment API", "Cloud"],
  },

  {
    icon: Smartphone,
    title: "Business Mobile Application",
    category: "Custom Mobile Software",
    description:
      "A connected mobile application designed to give customers and business users convenient access to services, information and digital workflows.",
    challenge:
      "Modern customers and distributed teams increasingly expect important services and business functions to be accessible through mobile devices.",
    solution:
      "A mobile-first application connected with secure APIs and cloud services provides users with fast access to relevant services, notifications and account information.",
    features: [
      "Customer Profiles",
      "Push Notifications",
      "Secure API Integration",
      "Cross-Platform Experience",
    ],
    technologies: ["React Native", "REST API", "Cloud", "Database"],
  },

  {
    icon: BarChart3,
    title: "Business Intelligence Dashboard",
    category: "Analytics & Reporting SaaS",
    description:
      "A centralized analytics platform designed to transform operational data into dashboards, business metrics and actionable reporting.",
    challenge:
      "Important business information becomes difficult to analyze when data is distributed across applications, departments and reporting sources.",
    solution:
      "Connected dashboards consolidate important KPIs, reports and operational metrics into a clear visual environment for better business visibility.",
    features: [
      "KPI Dashboards",
      "Visual Analytics",
      "Business Reports",
      "Performance Monitoring",
    ],
    technologies: ["React", "Charts", "REST API", "Database"],
  },
];


const capabilities = [
  {
    icon: Code2,
    title: "SaaS Development",
    text: "Cloud-based software platforms engineered around practical business workflows and long-term product growth.",
  },
  {
    icon: Layers3,
    title: "System Integration",
    text: "Connect SaaS products, APIs, databases, payment systems and third-party services into one digital ecosystem.",
  },
  {
    icon: Zap,
    title: "Performance & Reliability",
    text: "Software designed with speed, usability, maintainability and scalable architecture in mind.",
  },
  {
    icon: Rocket,
    title: "Continuous Product Growth",
    text: "Platforms that can evolve through new modules, integrations, automation and features as business requirements change.",
  },
];


const developmentProcess = [
  {
    number: "01",
    title: "Discover",
    text: "Understand your business model, users, operational challenges, workflows and software requirements.",
  },
  {
    number: "02",
    title: "Design",
    text: "Plan the product experience, technical architecture, database structure and scalable software workflow.",
  },
  {
    number: "03",
    title: "Develop",
    text: "Build, integrate and test the platform through a structured software engineering process.",
  },
  {
    number: "04",
    title: "Improve",
    text: "Deploy, monitor and continuously improve the product with new features, integrations and optimizations.",
  },
];


export default function Portfolio() {
  return (
    <main className="w-full overflow-hidden">

      <section className="relative w-full px-4 py-5 sm:px-6 lg:py-10">
        <div className="relative w-full">

          <div className="pointer-events-none absolute left-1/2 top-0 -z-10 h-[600px] w-[850px] -translate-x-1/2 rounded-full bg-pink-600/10 blur-[160px]" />

          <div className="mx-auto max-w-7xl">

            <div className="mx-auto max-w-5xl text-center">

              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-pink-400/20 bg-pink-400/5 px-5 py-2.5">

                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-pink-200">
                  SaaS & Software Portfolio
                </span>

              </div>

              <h1 className="text-4xl font-black leading-tight sm:text-6xl lg:text-7xl">

                SaaS Products & Business Software

                <br />

                <span className="bg-gradient-to-r from-pink-400 via-fuchsia-400 to-violet-400 bg-clip-text text-transparent">
                  Built for Real Operations
                </span>

              </h1>

              <p className="mx-auto mt-7 max-w-4xl text-base leading-8 text-slate-400 sm:text-lg">

                Explore SaaS platforms, ERP systems, HRMS, billing and
                invoicing software, business intelligence dashboards,
                e-commerce platforms and custom applications built by
                OjMee Technology to simplify operations and support
                business growth.

              </p>

              <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">

                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-pink-500 to-violet-600 px-7 py-4 font-bold text-white shadow-xl shadow-pink-500/20 transition hover:scale-105"
                >
                  Discuss Your Software Requirement

                  <ArrowRight className="h-5 w-5 transition group-hover:translate-x-1" />
                </Link>

                <Link
                  to="/products"
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-7 py-4 font-bold text-slate-200 transition hover:border-pink-400/30 hover:bg-white/10"
                >
                  Explore Our Products
                </Link>

              </div>

            </div>

          </div>

        </div>
      </section>

      <section className="relative w-full border-y border-white/5 bg-white/[0.015] px-4 py-20 sm:px-6 lg:py-20">

        <div className="pointer-events-none absolute right-0 top-1/3 -z-10 h-[500px] w-[500px] rounded-full bg-violet-600/10 blur-[150px]" />

        <div className="mx-auto max-w-7xl">

          <div className="mx-auto max-w-3xl text-center">

            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-violet-400/20 bg-violet-400/5 px-4 py-2">

              <Globe2 className="h-4 w-4 text-violet-300" />

              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-violet-200">
                Software We Build
              </span>

            </div>

            <h2 className="text-3xl font-black sm:text-5xl">

              Business Platforms Designed for

              <br />

              <span className="bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
                Real Operational Challenges
              </span>

            </h2>

            <p className="mt-5 text-sm leading-8 text-slate-500 sm:text-base">

              Our software combines product thinking, scalable architecture
              and practical business workflows to help organizations reduce
              manual work, centralize information and improve operational
              visibility.

            </p>

          </div>

          <div className="mt-14 space-y-6">

            {projects.map((project, index) => {
              const Icon = project.icon;

              return (
                <article
                  key={project.title}
                  className="group relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/[0.025] transition duration-500 hover:border-pink-400/25 hover:bg-white/[0.04]"
                >

                  <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-pink-500/10 opacity-0 blur-[100px] transition duration-700 group-hover:opacity-100" />


                  <div className="relative grid lg:grid-cols-[0.85fr_1.15fr]">

                    <div className="border-b border-white/10 p-7 sm:p-10 lg:border-b-0 lg:border-r">

                      <div className="flex items-start justify-between">

                        <div className="grid h-16 w-16 place-items-center rounded-2xl bg-gradient-to-br from-pink-500/15 to-violet-500/15 ring-1 ring-white/5">

                          <Icon className="h-7 w-7 text-pink-300" />

                        </div>

                        <span className="text-sm font-black tracking-[0.2em] text-slate-700">
                          {String(index + 1).padStart(2, "0")}
                        </span>

                      </div>


                      <div className="mt-8">

                        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-pink-300/80">
                          {project.category}
                        </span>

                        <h3 className="mt-3 text-2xl font-black sm:text-3xl">
                          {project.title}
                        </h3>

                        <p className="mt-4 text-sm leading-7 text-slate-400">
                          {project.description}
                        </p>

                      </div>

                      <div className="mt-8">

                        <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600">
                          Technology Stack
                        </p>

                        <div className="flex flex-wrap gap-2">

                          {project.technologies.map((tech) => (
                            <span
                              key={tech}
                              className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-400"
                            >
                              {tech}
                            </span>
                          ))}

                        </div>

                      </div>

                    </div>


                    <div className="p-7 sm:p-10">

                      <div className="grid gap-7 md:grid-cols-2">

                        <div>

                          <div className="mb-3 flex items-center gap-2">

                            <div className="h-1.5 w-1.5 rounded-full bg-pink-400" />

                            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
                              Business Challenge
                            </h4>

                          </div>

                          <p className="text-sm leading-7 text-slate-500">
                            {project.challenge}
                          </p>

                        </div>

                        <div>

                          <div className="mb-3 flex items-center gap-2">

                            <div className="h-1.5 w-1.5 rounded-full bg-violet-400" />

                            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
                              Our Software Approach
                            </h4>

                          </div>

                          <p className="text-sm leading-7 text-slate-500">
                            {project.solution}
                          </p>

                        </div>

                      </div>

                      <div className="mt-8 border-t border-white/10 pt-7">

                        <div className="mb-4 flex items-center gap-2">

                          <CheckCircle2 className="h-4 w-4 text-pink-400" />

                          <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
                            Key Capabilities
                          </h4>

                        </div>

                        <div className="grid gap-3 sm:grid-cols-2">

                          {project.features.map((feature) => (
                            <div
                              key={feature}
                              className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3"
                            >

                              <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400/70" />

                              <span className="text-xs text-slate-400">
                                {feature}
                              </span>

                            </div>
                          ))}

                        </div>

                      </div>

                      <div className="mt-8">

                        <Link
                          to="/contact"
                          className="group/link inline-flex items-center gap-2 text-sm font-bold text-pink-300"
                        >
                          Build a Similar Solution

                          <ArrowRight className="h-4 w-4 transition group-hover/link:translate-x-1" />

                        </Link>

                      </div>

                    </div>

                  </div>

                </article>
              );
            })}

          </div>

        </div>

      </section>

      <section className="w-full px-4 py-20 sm:px-6 lg:py-20">

        <div className="mx-auto max-w-7xl">

          <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">

            <div>

              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-pink-400/20 bg-pink-400/5 px-4 py-2">

                <Rocket className="h-4 w-4 text-pink-300" />

                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-pink-200">
                  SaaS Development Capabilities
                </span>

              </div>

              <h2 className="text-3xl font-black sm:text-5xl">

                From Business Requirement

                <br />

                <span className="bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
                  To Scalable SaaS Platform
                </span>

              </h2>

              <p className="mt-6 text-sm leading-8 text-slate-500 sm:text-base">

                Our software portfolio represents our ability to understand
                operational requirements, design scalable product
                architecture, develop connected applications and continuously
                improve software as businesses evolve.

              </p>

              <p className="mt-4 text-sm leading-8 text-slate-500 sm:text-base">

                From standalone business applications to connected SaaS
                ecosystems, our engineering approach focuses on usability,
                reliability, integrations and long-term product scalability.

              </p>

              <Link
                to="/contact"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-bold text-[#10091f] transition hover:bg-pink-100"
              >
                Discuss Your SaaS Idea

                <ArrowRight className="h-4 w-4" />
              </Link>

            </div>

            <div className="grid gap-4 sm:grid-cols-2">

              {capabilities.map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.title}
                    className="rounded-[2rem] border border-white/10 bg-white/[0.025] p-6 transition duration-300 hover:-translate-y-1 hover:border-violet-400/30"
                  >

                    <div className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-pink-500/10 to-violet-500/10">

                      <Icon className="h-5 w-5 text-violet-300" />

                    </div>

                    <h3 className="mt-5 text-lg font-black">
                      {item.title}
                    </h3>

                    <p className="mt-3 text-sm leading-7 text-slate-500">
                      {item.text}
                    </p>

                  </div>
                );
              })}

            </div>

          </div>

        </div>

      </section>


      <section className="w-full border-y border-white/5 bg-white/[0.015] px-4 py-20 sm:px-6 lg:py-20">

        <div className="mx-auto max-w-7xl">

          <div className="mx-auto max-w-3xl text-center">

            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-violet-400/20 bg-violet-400/5 px-4 py-2">

              <MonitorSmartphone className="h-4 w-4 text-violet-300" />

              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-violet-200">
                Our Product Engineering Process
              </span>

            </div>

            <h2 className="text-3xl font-black sm:text-5xl">

              Every Software Product Starts With

              <br />

              <span className="bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
                Understanding the Business
              </span>

            </h2>

            <p className="mt-5 text-sm leading-8 text-slate-500 sm:text-base">

              We begin with the business problem, users and operational
              workflow before deciding how the technology should be designed
              and implemented.

            </p>

          </div>

          <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">

            {developmentProcess.map((step) => (
              <div
                key={step.number}
                className="group relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.025] p-6 transition duration-300 hover:-translate-y-1 hover:border-pink-400/25"
              >

                <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-pink-500/[0.07] opacity-0 blur-3xl transition group-hover:opacity-100" />

                <div className="relative">

                  <div className="text-3xl font-black text-pink-400/50">
                    {step.number}
                  </div>

                  <h3 className="mt-5 text-xl font-black">
                    {step.title}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-slate-500">
                    {step.text}
                  </p>

                </div>

              </div>
            ))}

          </div>

        </div>

      </section>


      <section className="relative w-full px-4 py-20 sm:px-6 lg:py-24">

        <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[400px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-pink-600/[0.08] blur-[150px]" />

        <div className="mx-auto max-w-5xl">

          <div className="relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-gradient-to-br from-pink-500/[0.07] via-white/[0.025] to-violet-500/[0.07] px-6 py-12 text-center sm:px-10 lg:px-16 lg:py-16">

            <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-pink-500/10 blur-[100px]" />

            <div className="pointer-events-none absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-violet-500/10 blur-[100px]" />

            <div className="relative">

              <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-pink-300">
                Build With OjMee Technology
              </span>

              <h2 className="mx-auto mt-5 max-w-3xl text-3xl font-black leading-tight sm:text-5xl">

                Have a Business Process That

                <span className="bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
                  {" "}
                  Needs Better Software?
                </span>

              </h2>

              <p className="mx-auto mt-6 max-w-2xl text-sm leading-8 text-slate-400 sm:text-base">

                Whether you need a SaaS product, ERP, CRM, business
                automation platform, cloud application or custom software,
                our team can help turn your requirements into a scalable
                digital solution.

              </p>

              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">

                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-pink-500 to-violet-600 px-7 py-4 text-sm font-bold text-white shadow-xl shadow-pink-500/20 transition hover:scale-105"
                >
                  Discuss Your Requirement

                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                </Link>

                <Link
                  to="/products"
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-7 py-4 text-sm font-bold text-slate-200 transition hover:border-pink-400/30 hover:bg-white/10"
                >
                  View SaaS Products
                </Link>

              </div>

            </div>

          </div>

        </div>

      </section>


      <section className="relative w-full border-t border-white/5 bg-white/[0.015] px-4 py-20 sm:px-6 lg:py-0">

        <FAQSection />

      </section>

    </main>
  );
}